import React, { createRef } from "react";
import moment from "moment";
import { isEqual } from "lodash";
import fieldPropTypes from "./field-prop-types";
import { fieldValidation as validation } from "../utils/field-validation";
import {
  DermTreatmentHeader,
  FIELD_PARAMS,
  FIELD_NAMES
} from "../utils/constants";
import CreditCard from "./form-text-fields/credit-card";
import PhonePicker from "../theme/default/phone-picker";
import { idCreator } from "../utils/id-creator";
import ValidationMessage from "../theme/default/validation-message";
import { UNIREG_MASK_KEY } from "../theme/default/masks";
import { MessagingContext } from "../context/messaging-layer";

const LOCATION_FIELDS = {
  country_cd: true,
  state: true,
  can_state: true
};

class FormTextField extends React.Component {
  constructor(props) {
    super(props);
    const { field, appData, value } = props;
    const { params } = field;

    this.wrapperRef = createRef();
    this.updateControlValidation(field, appData, value);
    this.params = params.split("|").map(p => p.replace("TDFieldOption", ""));
    this.renderCalendarPicker = this.renderCalendarPicker.bind(this);
    this.renderDatePicker = this.renderDatePicker.bind(this);
    this.renderCreditCard = this.renderCreditCard.bind(this);
    this.renderAutocomplete = this.renderAutocomplete.bind(this);
    this.renderSelect = this.renderSelect.bind(this);
    this.renderTextField = this.renderTextField.bind(this);
    this.renderInput = this.renderInput.bind(this);
    this.renderSlider = this.renderSlider.bind(this);
    this.renderTextArea = this.renderTextArea.bind(this);
    this.renderRadioButtons = this.renderRadioButtons.bind(this);
    this.renderPhonePicker = this.renderPhonePicker.bind(this);
    this.updateValueObject = this.updateValueObject.bind(this);
    this.updateFieldValidation = this.updateFieldValidation.bind(this);
  }

  componentDidMount() {
    const {
      field,
      field: { name, params },
      appData: { values, handleSelectChange, handleChange }
    } = this.props;

    if (
      LOCATION_FIELDS[name] &&
      (values[name] === "" || values[name].value === "")
    ) {
      handleSelectChange({ [name]: "currentLocation" }, field);
    }

    if (params.includes("TDFieldOptionPassword")) {
      handleChange({ [name]: "" });
    }

    this.resetFieldValidation();
  }

  componentDidUpdate() {
    const { field, appData, value } = this.props;
    this.updateControlValidation(field, appData, value);
  }

  updateValue = e => {
    e.preventDefault();
    const {
      appData: { values, handleChange, isUnifiedRegistration }
    } = this.props;
    const { value } = e.target;
    const name = e.target.getAttribute("name") || "";

    if (
      value !== values[name] ||
      (isUnifiedRegistration && e.type === "blur")
    ) {
      handleChange({ [name]: value });
      this.updateFieldValidation(e);
    }
  };

  getMaskExtraParam = () => {
    const {
      appData: { isUnifiedRegistration, brandCountryCode },
      field: { params, data }
    } = this.props;
    if (data?.locale) {
      return data.locale;
    }
    if (params.includes("DateOfBirth") && isUnifiedRegistration) {
      return brandCountryCode;
    }
    if (params.includes("PhoneNumber") && isUnifiedRegistration) {
      return UNIREG_MASK_KEY;
    }
  };

  extractConfig = (config, extraParam) =>
    this.params.reduce(
      (configExtended, fieldParam) =>
        config[fieldParam] && config[fieldParam].hasNesting
          ? Object.assign(
              configExtended,
              config[fieldParam][extraParam] || config[fieldParam].default
            )
          : Object.assign(configExtended, config[fieldParam]),
      {}
    );

  isIncludesInFieldProps = prop =>
    this.props.field?.params?.split("|")?.includes(prop);

  updateControlValidation(field, appData) {
    const isFieldVisible =
      !appData.assesmentsState[field.name] ||
      appData.assesmentsState[field.name].visible;

    if (isFieldVisible) {
      validation[field.type].update(field, appData, {
        fieldRef: this.wrapperRef
      });
    }
  }

  submitForm = event => {
    if (event.keyCode === 13) {
      const {
        context,
        props: { field, appData, screen }
      } = this;

      handleClickEvent(field, screen, appData, context);
      return false;
    }
  };
  updateValueObject(name, value) {
    const {
      appData: { values, resetFieldValidation, handleChange }
    } = this.props;
    if (!isEqual(value, values[name])) {
      resetFieldValidation(name);
      handleChange({ [name]: value });
    }
  }

  preventFormCompletion(event) {
    if (event.keyCode === 13) {
      event.preventDefault();
      return false;
    }
  }

  filterUnselectedOptions() {
    const {
      appData: { values },
      field
    } = this.props;

    const selectedGroupValues = Object.keys(values).reduce(
      (groupValues, fieldName) => {
        if (fieldName.includes(field.data.group) && fieldName !== field.name) {
          groupValues.push(values[fieldName]);
        }

        return groupValues;
      },
      []
    );

    return field.options.filter(
      option =>
        !selectedGroupValues.includes(
          option instanceof Object ? option.value || option.text : option
        )
    );
  }

  formatFieldOptions() {
    const {
      appData: { history, values },
      field
    } = this.props;

    if (
      !(Array.isArray(field.options) && field.options.length) &&
      !(
        Array.isArray(field.data && field.data.search_data) &&
        field.data.search_data.length
      )
    ) {
      return false;
    }

    if (field.name === "term") {
      return field.options;
    }

    if (LOCATION_FIELDS[field.name]) {
      field.options = field.options ? field.options : field.data.search_data;
    }

    // Delete all options except preselected
    if (
      history[0].name === "ConsultationPhotoModal" &&
      (history[1].name === "MhMinorManualIntake" ||
        history[1].name === "VisitMhMinorConsent") &&
      field.name.includes("attachment_type_cd")
    ) {
      return field.options
        .filter(option => option.value === values[field.name])
        .map(option => ({ label: option.text, value: option.value }));
    }

    let options = [...field.options];
    // Filter selected options withing group of selects
    if (
      field.params.includes("TDFieldOptionDropdownFilterSelectedOptions") &&
      field.data.group
    ) {
      options = this.filterUnselectedOptions();
    }

    // Format objects in option array with properties { label, value }
    return options
      .filter(option => (option instanceof Object ? !option.hidden : true))
      .map(option => {
        if (option instanceof Object) {
          if (
            option.value ||
            history[0].name === "ConsultationEnhancedSchedulingFilter"
          ) {
            return { label: option.text, value: option.value };
          } else {
            return { label: option.text, value: option.text };
          }
        } else {
          return { label: option, value: option };
        }
      });
  }

  resetFieldValidation(value, isSelect) {
    const { name } = this.props.field;
    const {
      resetFieldValidation,
      resetFieldValidationAll,
      isPulse,
      isEnhancedRedesign,
      isUnifiedRegistration
    } = this.props.appData;
    resetFieldValidation(name);
    const shouldResetAllErrors =
      isSelect &&
      (isEnhancedRedesign || isPulse || isUnifiedRegistration) &&
      name === FIELD_NAMES.searchBy &&
      value !== this.props.value;
    if (shouldResetAllErrors) {
      resetFieldValidationAll();
    }
  }

  updateFieldValidation(e) {
    const {
      field,
      appData,
      appData: {
        setFieldValidation,
        resetFieldValidation,
        isUnifiedRegistration
      }
    } = this.props;

    if (isUnifiedRegistration && e.type === "blur") {
      const validatedField = validation[field.type].update(field, appData, {
        fieldRef: this.wrapperRef
      });
      setFieldValidation({ [field.name]: validatedField });
      this.updateFormGroupErrors(field.name, validatedField);
    } else {
      resetFieldValidation(field.name);
    }
  }

  updateValueWrapper(...args) {
    this.resetFieldValidation();
    return this.props.appData.updateValue(...args);
  }

  isInvalid() {
    const { name } = this.props.field;
    const { fieldValidation } = this.props.appData;
    return fieldValidation[name] && !fieldValidation[name].isValid;
  }

  updateFormGroupErrors(fieldName, validatedField) {
    const {
      appData: { isUnifiedRegistration, formGroupErrors, setFormGroupErrors }
    } = this.props;
    if (isUnifiedRegistration && formGroupErrors.length) {
      if (!validatedField || validatedField.isValid) {
        const actualErrors = formGroupErrors.filter(
          error => error.fieldName !== fieldName
        );
        setFormGroupErrors(actualErrors);
      }
    }
  }

  renderField(invalid) {
    const {
      field: { name, title },
      appData
    } = this.props;
    const {
      data: { screens },
      screenIndex
    } = appData;

    const textAreaFieldNames = [
      "SECTIONMNEMONIC_VPCINTAKE_HEALTH_GOALS",
      "SECTIONMNEMONIC_VPCINTAKE_SURGERIES",
      "SECTIONMNEMONIC_NUTINTAKE_SUPPLEMENT",
      "SECTIONMNEMONIC_NUTINTAKE_DINEOUT",
      "SECTIONMNEMONIC_NUTINTAKE_ADDITIONAL",
      "SECTIONMNEMONIC_NUTINTAKE_MEALS",
      "vitalsNote"
    ];

    if (
      screens[screenIndex].name !== "PulseConsultationPhotoModal" &&
      name.includes("photoName") &&
      title === "Description"
    ) {
      textAreaFieldNames.push(name);
    }

    const options = this.formatFieldOptions();

    if (this.isIncludesInFieldProps("TDFieldOptionCalendarPicker")) {
      return this.renderCalendarPicker(invalid);
    }

    if (this.isIncludesInFieldProps("TDFieldOptionDatePicker")) {
      return this.renderDatePicker();
    }

    if (this.isIncludesInFieldProps("TDFieldOptionCreditCard")) {
      return this.renderCreditCard();
    }

    if (this.isIncludesInFieldProps("TDFieldOptionCountryPhoneNumber")) {
      return this.renderPhonePicker();
    }

    if (this.isIncludesInFieldProps("TDFieldOptionTextArea")) {
      return this.renderTextArea(invalid);
    }

    const isFieldVisible =
      !appData.assesmentsState[name] || appData.assesmentsState[name].visible;

    if (!isFieldVisible) {
      return null;
    }
    const screenName = screens[screenIndex].name;
    return options
      ? this.isIncludesInFieldProps("TDFieldOptionSearch")
        ? this.renderAutocomplete()
        : screens[screenIndex].name.includes("ConsultationVpcAssessment")
        ? this.renderSlider()
        : screenName === "VisitUscStudyAttestation"
        ? this.renderRadioButtons()
        : name === "reason_code" // Cancel Visit screen
        ? this.renderSelectWithChips()
        : this.renderSelect()
      : !this.isIncludesInFieldProps("TDFieldOptionSearch")
      ? textAreaFieldNames.some(v => name.includes(v))
        ? this.renderTextArea(invalid)
        : this.renderTextField()
      : this.renderInput();
  }

  renderCalendarPicker(invalid) {
    const {
      components,
      appData: { handleChange, values, isPulse, isEnhancedRedesign },
      field: { name, title, params, accessibility = {} },
      appData
    } = this.props;
    const { startDateInterval, endDateInterval } = {
      ...this.props.field?.action?.data
    };

    const { Label, DatePicker } = components;
    const { label: textLabel } = accessibility;
    const onDatePickerChange = date => {
      this.resetFieldValidation();
      handleChange({ [name]: date ? date.toDateString() : "" });
    };

    let minDate, maxDate;
    if (
      typeof startDateInterval !== "undefined" &&
      typeof endDateInterval !== "undefined" &&
      startDateInterval <= endDateInterval
    ) {
      minDate = new Date();
      maxDate = new Date();

      minDate.setSeconds(minDate.getSeconds() + startDateInterval);
      maxDate.setSeconds(maxDate.getSeconds() + endDateInterval);
    }

    return (
      <>
        <Label
          name={name}
          text={title}
          isRequired={this.isRequired}
          params={params}
          invalid={invalid}
          appData={appData}
          srLabel={textLabel}
        >
          <DatePicker
            value={values[name] ? new Date(values[name]) : null}
            minDate={minDate ?? moment().startOf("day").toDate()}
            maxDate={maxDate}
            initialMonth={values[name] ? new Date(values[name]) : new Date()}
            onChange={onDatePickerChange}
            invalid={invalid}
            components={components}
            usePulseStyles={isEnhancedRedesign || isPulse}
            appData={appData}
            inputAriaLabel={textLabel}
            name={title}
          />
        </Label>
      </>
    );
  }

  renderDatePicker() {
    const {
      components,
      appData: { handleValuesChange, values, settings },
      field: { action, title }
    } = this.props;
    const { BasicTimePicker } = components;

    const optionalProps = {};
    if (action && action.data) {
      ["minHour", "maxHour", "scheduleDelay"].forEach(propName => {
        if (action.data[propName]) {
          optionalProps[propName] = parseInt(action.data[propName], 10);
        }
      });
    }

    return (
      <BasicTimePicker
        {...optionalProps}
        title={title}
        value={{
          type: values.__mode,
          hour: values.__hour,
          minute: values.__minute,
          ampm: values.__ampm
        }}
        onChange={newValues =>
          handleValuesChange({
            __mode: newValues.type,
            __hour: newValues.hour,
            __minute: newValues.minute,
            __ampm: newValues.ampm
          })
        }
        components={components}
        settings={settings}
        params={this.props.field.params}
        time_zone={values?.time_zone}
      />
    );
  }

  renderAutocomplete() {
    const {
      components,
      appData: { handleSelectChange, values, settings },
      field,
      field: {
        name,
        title,
        __allowManual,
        params,
        accessibility = {},
        placeholder
      },
      appData
    } = this.props;
    const { Label, Autocomplete } = components;
    const { label: textLabel } = accessibility;
    const options = this.formatFieldOptions();
    const { labelId, inputId, listId } = idCreator(name);
    return (
      <>
        <Label
          name={name}
          id={labelId}
          inputId={inputId}
          text={title}
          isRequired={this.isRequired}
          params={params}
          invalid={this.isInvalid()}
          appData={appData}
          srLabel={textLabel}
        />
        <Autocomplete
          label={title}
          labelId={labelId}
          inputId={inputId}
          listId={listId}
          suggestions={options}
          placeholder={placeholder}
          value={values[name]}
          handleChange={v => {
            this.resetFieldValidation();
            return handleSelectChange({ [name]: v }, field);
          }}
          onEnterDown={this.preventFormCompletion}
          name={name}
          locationField={LOCATION_FIELDS[name]}
          allowManual={__allowManual}
          invalid={this.isInvalid()}
          showMagnifyingGlassIcon={params.includes("TDFieldOptionIconSearch")}
          showCrossIcon={params.includes("TDFieldOptionIconClearInput")}
          settings={settings}
          params={params}
        />
      </>
    );
  }

  renderSelectWithChips() {
    const {
      components,
      appData: { handleSelectChange, fieldAttributes },
      field: { name },
      value
    } = this.props;
    const { SelectWithChips } = components;
    const options = this.formatFieldOptions();
    return (
      <SelectWithChips
        name={name}
        options={options}
        defaultOption={value}
        onChange={v => handleSelectChange({ [name]: v })}
        fieldAttributes={fieldAttributes}
        onEnterDown={this.preventFormCompletion}
      />
    );
  }

  renderSelect() {
    const {
      components,
      appData: { handleSelectChange, fieldAttributes: attr, values },
      field,
      field: {
        name,
        text,
        title,
        params,
        runFieldAction,
        placeholder,
        accessibility = {}
      },
      value,
      appData
    } = this.props;

    const { Label, Select } = components;
    const { label: textLabel } = accessibility;

    const attributes = this.extractConfig(attr);
    const { labelId, inputId, listId } = idCreator(name);
    const fieldAttributes = {
      name,
      value: DermTreatmentHeader.includes(name) ? values.term : value,
      defaultValue: text,
      ...attributes
    };

    const options = this.formatFieldOptions();

    return (
      <>
        <Label
          name={name}
          id={labelId}
          inputId={inputId}
          text={title}
          isRequired={this.isRequired}
          params={params}
          invalid={this.isInvalid()}
          appData={appData}
          srLabel={textLabel}
        />
        <Select
          appData={appData}
          name={name}
          options={options}
          id={inputId}
          labelId={labelId}
          listId={listId}
          selectedOption={field.options.find(o => o.selected)?.value || value}
          onChange={v => {
            this.resetFieldValidation(v, true);
            return handleSelectChange({ [name]: v }, field);
          }}
          onBlur={this.updateFieldValidation}
          fieldAttributes={fieldAttributes}
          onEnterDown={this.preventFormCompletion}
          invalid={this.isInvalid()}
          runFieldAction={runFieldAction}
          placeholder={placeholder}
          disabled={params.includes("TDFieldOptionLocked")}
          hideSelectedOptions={params.includes(
            "TDFieldOptionDropdownFilterSelectedOptions"
          )}
          autoCompleteDisabled={params.includes(
            "TDFieldOptionDisableAutoComplete"
          )}
        />
      </>
    );
  }

  renderRadioButtons() {
    const {
      components,
      appData: { handleSelectChange },
      field: { name, title, params },
      value
    } = this.props;
    const { Label, Radio } = components;
    const options = this.formatFieldOptions();
    return (
      <>
        <Label name={name} text={title} params={params} />
        {options
          .filter(option => option.label)
          .map((option, index) => (
            <Radio
              key={option.label}
              name={`usc-attestation-${option.value}`}
              label={option.label}
              checked={option.value === value}
              onChange={() => {
                this.resetFieldValidation();
                return handleSelectChange(
                  { [name]: option.value },
                  this.props.field
                );
              }}
              invalid={this.isInvalid()}
              index={index}
              fieldsName={name}
            />
          ))}
      </>
    );
  }

  renderTextField() {
    const {
      components,
      appData,
      appData: {
        masks,
        fieldAttributes: attr,
        values,
        settings,
        isUnifiedRegistration
      },
      field: {
        name,
        text,
        title,
        params,
        placeholder,
        accessibility = {},
        data = {}
      },
      value
    } = this.props;
    const { Label, TextField, Text } = components;
    const { label: textLabel } = accessibility;
    const { labelId, inputId } = idCreator(name);
    const maskExtraParam = this.getMaskExtraParam();
    const maskingAttributes = this.extractConfig(masks, maskExtraParam);
    const attributes = this.extractConfig(attr);

    const fieldAttributes = {
      name,
      type: !params.includes("TDFieldOptionPassword") ? "text" : "password",
      value: DermTreatmentHeader.includes(name)
        ? values.term
        : value || values[name] || text,
      defaultValue: text,
      ...attributes,
      autoComplete: "off"
    };

    return (
      <>
        <Label
          name={name}
          id={labelId}
          inputId={inputId}
          text={title}
          isRequired={this.isRequired}
          params={params}
          invalid={this.isInvalid()}
          appData={appData}
          srLabel={textLabel}
        >
          {data?.locale && maskingAttributes.placeholder && (
            // eslint-disable-next-line react/jsx-indent
            <Text name={name} id={`${name}-helper-text`}>
              {maskingAttributes.placeholder}
            </Text>
          )}
        </Label>
        <TextField
          name={name}
          id={inputId}
          labelId={labelId}
          fieldAttributes={fieldAttributes}
          maskingAttributes={maskingAttributes}
          placeholder={placeholder}
          updateValue={this.updateValue}
          onEnterDown={this.preventFormCompletion}
          invalid={this.isInvalid()}
          disabled={params.includes("TDFieldOptionLocked")}
          params={params}
          data={data}
          translations={settings?.translations}
          isUnifiedRegistration={isUnifiedRegistration}
          passwordValue={this.props.password}
        />
      </>
    );
  }

  renderInput() {
    const {
      components,
      field: { name, accessibility = {} }
    } = this.props;
    const { Input } = components;
    const { label: ariaLabel } = accessibility;
    this.asterisk = this.isRequired ? "*" : "";

    return (
      <Input
        name={name + this.asterisk}
        ariaLabel={ariaLabel}
        onChange={this.updateValueWrapper}
        onEnterDown={this.submitForm}
        invalid={this.isInvalid()}
      />
    );
  }

  renderSlider() {
    const {
      components,
      field,
      field: { name, title, params, accessibility = {} },
      appData: { resetFieldValidation, handleSliderChange, values },
      appData
    } = this.props;
    const { Label, Slider } = components;
    const { label: textLabel } = accessibility;
    const invalid = this.isInvalid();

    return (
      <>
        <Label
          name={name}
          text={title}
          isRequired={this.isRequired}
          params={params}
          appData={appData}
          srLabel={textLabel}
        />
        <Slider
          handleChange={(fielditem, option) => {
            resetFieldValidation(name);
            handleSliderChange(fielditem, option);
          }}
          field={{ value: values[name], ...field }}
          components={components}
          invalid={invalid}
          isRadioGroup
          label={title}
        />
      </>
    );
  }

  renderTextArea(invalid) {
    const {
      components,
      field: { name, title, params, placeholder, accessibility = {} },
      appData: { values },
      appData
    } = this.props;
    const { Label, TextArea } = components;
    const { label: textLabel } = accessibility;
    const { labelId, textAreaId } = idCreator(name);

    return (
      <Label
        name={name}
        text={title}
        isRequired={this.isRequired}
        id={labelId}
        inputId={textAreaId}
        params={params}
        appData={appData}
        srLabel={textLabel}
      >
        <TextArea
          name={name}
          onChange={this.updateValue}
          value={values[name]}
          placeholder={placeholder}
          invalid={invalid}
          params={params}
          id={textAreaId}
          labelId={labelId}
          appData={appData}
        />
      </Label>
    );
  }

  renderCreditCard() {
    const {
      components,
      appData: { fieldAttributes: attr, values },
      field: { name, text, title, params, placeholder, accessibility = {} },
      value,
      appData
    } = this.props;

    const { label: textLabel } = accessibility;
    const attributes = this.extractConfig(attr);

    const fieldAttributes = {
      name,
      value: DermTreatmentHeader.includes(name) ? values.term : value,
      defaultValue: text,
      ...attributes
    };

    return (
      <CreditCard
        components={components}
        name={name}
        title={title}
        isRequired={this.isRequired}
        params={params}
        fieldAttributes={fieldAttributes}
        placeholder={placeholder}
        onUpdateValue={this.updateValue}
        onEnterDown={this.preventFormCompletion}
        isInvalid={this.isInvalid()}
        appData={appData}
        srLabel={textLabel}
      />
    );
  }

  renderPhonePicker() {
    const {
      components,
      appData: { values },
      field: { name, title, params, options, data }
    } = this.props;

    return (
      <PhonePicker
        components={components}
        options={options}
        name={name}
        data={data}
        title={title}
        isRequired={this.isRequired}
        params={params}
        values={values}
        onUpdate={this.updateValueObject}
        isInvalid={this.isInvalid()}
      />
    );
  }

  render() {
    const { params, title, name } = this.props.field;
    this.isRequired =
      this.props.appData.showAsterisk &&
      !params.includes("IsOptional") &&
      !params.includes(FIELD_PARAMS.fieldOptionHidden) &&
      title;

    const isInvalid = this.isInvalid();
    const errors = this.props.appData.fieldValidation?.[name];
    const { errorMessage } = this.props.appData.fieldValidation?.[name] ?? {};
    const visibilityClass = params.includes(FIELD_PARAMS.fieldOptionHidden)
      ? "hidden-field"
      : "";

    const showInlineMessage = name !== "reason_code";

    return (
      <div
        className={`field-wrapper-web-sdk ${visibilityClass}`}
        ref={this.wrapperRef}
      >
        {this.renderField(isInvalid)}
        {showInlineMessage && (
          <ValidationMessage
            fieldName={name}
            isInvalid={isInvalid}
            isUnifiedRegistration={this.props.appData.isUnifiedRegistration}
          >
            {isInvalid && errors && !errorMessage
              ? errors.map(error => error.errorMessage)
              : errorMessage}
          </ValidationMessage>
        )}
      </div>
    );
  }
}

FormTextField.propTypes = fieldPropTypes;
FormTextField.contextType = MessagingContext;

export default FormTextField;
